// NeonOracleScenarioSim — themed renderer for the `neon-oracle` theme.
//
// A faithful React port of the "NEON ORACLE" design-exploration mockup
// (design-explorations/scenario-themes/neon-oracle/index.html), driven by the
// REAL scenario-simulation branch data. Rendered by ScenarioSimulationSlide
// when the active theme is `neon-oracle`.
//
// REFACTORED onto the shared theme-world toolkit (lib/scenario-themes/): the
// world's atmosphere layers (<ThemeWorldAtmosphere>), Web Audio (useThemeWorldAudio),
// typewriter (<Typewriter>) and sound toggle (<SoundToggle>) now come from the
// toolkit, shared with the Obsidian renderer. Only the component-specific chrome
// (decision-log HUD, terminal framing, glitch-develop ending reveal, choice rows)
// keeps its own scoped CSS here. Behaviour/look unchanged.
//
// State machine mirrors ScenarioSimulationSlide's `Student`; the CAPTURE
// contract diverges deliberately (handover §8.8): activity records are keyed
// per slide, so per-choice writes overwrote each other and only the final
// decision survived. This skin writes ONE composite journey record —
// { path, endingId, reflection? } — at the ending (and again when the
// reflection is logged), mirrors HyperpopDragSort's single-payload hygiene,
// and rehydrates a returning learner straight into the settled ending view.

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { ScenarioSimulationSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { WorldStage } from '../../../../../lib/scenario-themes/WorldStage';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';

type Branch = ScenarioSimulationSlideData['branches'][number];
type Choice = NonNullable<Branch['choices']>[number];

const STYLE_ID = 'noracle-scoped-styles';

// ─────────────────────────────────────────────────────────────────────────────
// Component-specific scoped CSS — HUD / terminal / glitch reveal / choices.
// (Atmosphere layers now live in the toolkit's <ThemeWorldAtmosphere>.)
// All rules namespaced under `.noracle`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(lime: string, cyan: string): string {
  return `
.noracle { position: relative; }
.noracle * { box-sizing: border-box; }

/* holographic settle on transition — a soft focus-pull, not a CRT shudder */
.noracle .no-stage.no-glitching { animation: noHoloSettle 0.5s ease 1; }
@keyframes noHoloSettle {
  0% { opacity: 0.55; filter: brightness(1.5) blur(1.5px); transform: scale(1.006); }
  55% { opacity: 1; filter: brightness(1.08) blur(0); }
  100% { filter: none; transform: none; }
}

/* ===== HUD : DECISION LOG ===== */
.noracle .no-beat { transition: all 0.35s; }
.noracle .no-beat.no-current { animation: noBeatPulse 2.2s ease-in-out infinite; }
@keyframes noBeatPulse {
  0%,100% { box-shadow: 0 0 18px ${lime}29, inset 0 0 24px ${lime}0d; }
  50% { box-shadow: 0 0 28px ${lime}4d, inset 0 0 30px ${lime}17; }
}

/* ===== CONSOLE ===== */
.noracle .no-blip::before {
  content: ""; display: inline-block; width: 8px; height: 8px; margin-right: 10px;
  background: ${cyan}; box-shadow: 0 0 10px ${cyan}80; vertical-align: middle;
  animation: noBlip 1.4s steps(2) infinite;
}
@keyframes noBlip { 0%,60%{opacity:1;} 61%,100%{opacity:0.15;} }

.noracle .no-question { opacity: 0; transform: translateY(8px); transition: opacity 0.5s, transform 0.5s; }
.noracle .no-question.no-show { opacity: 1; transform: none; }

.noracle .no-choice {
  transition: all 0.16s ease;
  opacity: 0; transform: translateX(-10px);
}
.noracle .no-choice.no-reveal { opacity: 1; transform: none; }
.noracle .no-choice:hover, .noracle .no-choice:focus-visible {
  border-color: ${lime} !important; color: #fff !important; outline: none;
  background: ${lime}12 !important;
  box-shadow: 0 0 22px ${lime}24, inset 0 0 0 1px ${lime}33;
  transform: translateX(4px);
}
.noracle .no-choice:hover .no-key, .noracle .no-choice:focus-visible .no-key { color: ${lime} !important; text-shadow: 0 0 8px ${lime}8c; }
.noracle .no-choice:hover .no-arrow, .noracle .no-choice:focus-visible .no-arrow { opacity: 1 !important; color: ${lime} !important; transform: translateX(3px); }

/* ending scanline reveal */
.noracle .no-reveal-wrap.no-developing { animation: noDevelopIn 1.5s ease forwards; }
@keyframes noDevelopIn {
  0% { clip-path: inset(0 0 100% 0); filter: brightness(1.6) blur(2px); opacity: 0.85; }
  60% { filter: brightness(1.15) blur(0.4px); opacity: 1; }
  100% { clip-path: inset(0 0 0% 0); filter: none; opacity: 1; }
}
.noracle .no-reveal-wrap.no-developing::after {
  content: ""; position: absolute; left: -6%; right: -6%; height: 2px;
  background: linear-gradient(90deg, transparent, ${cyan}, #eafdff, ${cyan}, transparent);
  box-shadow: 0 0 22px 6px ${cyan}80;
  animation: noSweepLine 1.5s ease forwards; z-index: 5;
}
@keyframes noSweepLine { 0% { top: 0; opacity: 1; } 95% { opacity: 1; } 100% { top: 100%; opacity: 0; } }

.noracle .no-restart { opacity: 0; transform: translateY(8px); transition: all 0.18s, opacity 0.4s, transform 0.4s; }
.noracle .no-restart.no-show { opacity: 1; transform: none; }
.noracle .no-restart:hover { background: ${lime} !important; color: #05060a !important; box-shadow: 0 0 30px ${lime}66; }

.noracle .no-dot {
  display: inline-block; width: 6px; height: 6px; background: ${lime};
  box-shadow: 0 0 8px ${lime}8c; animation: noBlip 2s steps(2) infinite;
}

/* reduced motion */
@media (prefers-reduced-motion: reduce) {
  .noracle .no-beat.no-current, .noracle .no-blip::before, .noracle .no-dot { animation: none !important; }
  .noracle .no-reveal-wrap.no-developing { animation: none !important; clip-path: none !important; filter: none !important; }
  .noracle .no-reveal-wrap.no-developing::after { display: none; }
  .noracle .no-stage.no-glitching { animation: none !important; }
  .noracle .no-question, .noracle .no-choice, .noracle .no-restart { transition: none !important; }
}
`;
}

function useScopedStyles(lime: string, cyan: string): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss(lime, cyan);
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // Token values are stable per theme; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Journey capture — one composite record per completed run.
// ─────────────────────────────────────────────────────────────────────────────

type PathStep = { branchId: string; choiceLetter: string; pathLabel: string };
type SavedJourney = { path: PathStep[]; endingId: string; reflection?: string };

/**
 * Parse a saved composite payload back into a journey. Every step must fully
 * validate — a partial path would render a misleading oracle.log, so any
 * malformed step rejects the whole payload (restore nothing over restoring
 * wrong, matching HyperpopDragSort's strictness).
 */
function parseSavedJourney(saved: Record<string, unknown> | null | undefined): SavedJourney | null {
  if (!saved || !Array.isArray(saved.path) || typeof saved.endingId !== 'string' || !saved.endingId) {
    return null;
  }
  const path: PathStep[] = [];
  for (const raw of saved.path) {
    if (
      !raw ||
      typeof raw !== 'object' ||
      typeof (raw as { branchId?: unknown }).branchId !== 'string' ||
      typeof (raw as { choiceLetter?: unknown }).choiceLetter !== 'string' ||
      typeof (raw as { pathLabel?: unknown }).pathLabel !== 'string'
    ) {
      return null;
    }
    path.push(raw as PathStep);
  }
  return {
    path,
    endingId: saved.endingId,
    reflection: typeof saved.reflection === 'string' ? saved.reflection : undefined,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function NeonOracleScenarioSim({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ScenarioSimulationSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();

  // Theme tokens (lime = primary, cyan = accent).
  const lime = theme.colors.primary;
  const cyan = theme.colors.accent || theme.colors.primary;
  const headingFont = theme.fonts.heading;
  // Prose face for anything the learner READS (situation, choices, endings).
  const bodyFont = theme.fonts.body;
  // Mono face for terminal chrome — labels, the log HUD, status strip, readouts.
  // These were one font before: setting the whole world, prose included, in
  // monospace. Splitting the roles keeps the terminal identity in the chrome
  // where it belongs and gets the reading text out of it.
  const monoFont = theme.fonts.code || theme.fonts.body;

  useScopedStyles(lime, cyan);

  // ── Audio (toolkit) ──────────────────────────────────────────────────────────
  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('neon-oracle');

  // ── State machine (mirrors ScenarioSimulationSlide's Student) ────────────────
  const startId = data.branches.find((b) => b.id === 'start')?.id ?? data.branches[0]?.id ?? '';

  // A saved composite journey restores the learner straight into the settled
  // ending view (done-state rehydration, like RisographPerspectiveLenses) —
  // never mid-path. Only honoured when the saved endingId still lands terminal
  // against the CURRENT branch data; if the author has since rewired the ending
  // into a live decision, starting over beats restoring a wrong state.
  const savedJourney = useMemo(() => {
    const journey = parseSavedJourney(savedResponse);
    if (!journey) return null;
    const branch = data.branches.find((b) => b.id === journey.endingId);
    const stillTerminal =
      !branch ||
      branch.type === 'ending' ||
      (branch.type === 'decision' && (branch.choices?.length ?? 0) === 0);
    if (!stillTerminal) return null;
    // A missing endingId with no path would collide with the broken-start guard.
    if (!branch && journey.path.length === 0) return null;
    return journey;
  }, [savedResponse, data.branches]);

  const [currentBranchId, setCurrentBranchId] = useState<string>(savedJourney?.endingId ?? startId);
  const [path, setPath] = useState<PathStep[]>(savedJourney?.path ?? []);

  const currentBranch = data.branches.find((b) => b.id === currentBranchId);

  // ── Transient view state (reveals / settle) ──────────────────────────────────
  // Which branch has finished typing — NOT a bare boolean.
  //
  // It was `useState(false)` reset from the per-branch effect, and child effects
  // run before parent ones: on mount the Typewriter fired onDone (immediately,
  // under prefers-reduced-motion) and set it true, then the parent effect below
  // set it straight back to false. Nothing ever fired onDone again, so a
  // reduced-motion learner sat looking at a terminal whose question and choices
  // were permanently at opacity 0. Keying it to the branch makes the reveal
  // immune to effect ordering.
  const [typedBranch, setTypedBranch] = useState<string | null>(null);
  const typingDone = typedBranch === currentBranchId;
  // Bumped by the header's "skip ▸" control to finish the typewriter at once.
  const [skipSignal, setSkipSignal] = useState(0);
  const [glitching, setGlitching] = useState(false);
  const [endingDeveloping, setEndingDeveloping] = useState(false);
  const [restartShow, setRestartShow] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Pre-seed both guards when restoring a saved journey: the composite record
  // and onComplete must not re-fire for an ending the learner already reached.
  const completedRef = useRef(savedJourney != null);
  const restoredRef = useRef(savedJourney != null);

  // ── Reflection commit state ──────────────────────────────────────────────────
  const [reflectionText, setReflectionText] = useState<string>(savedJourney?.reflection ?? '');
  const [reflectionLogged, setReflectionLogged] = useState<boolean>(savedJourney?.reflection != null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);
  const later = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // The full source string the <Typewriter> types out for a decision branch.
  const decisionText = currentBranch
    ? [currentBranch.situation, currentBranch.consequence].filter(Boolean).join('\n\n')
    : '';

  // Drive the per-branch reveal sequence (typewriter for decisions, scanline
  // sweep for endings). Re-runs whenever the branch changes.
  useEffect(() => {
    clearTimers();
    // NB: no `setTypedBranch(null)` here. It's derived per branch, so a stale
    // value can't leak across a transition, and resetting it from this effect
    // is exactly what used to stomp the Typewriter's onDone on mount.
    setEndingDeveloping(false);
    setRestartShow(false);

    // A genuinely broken START (no current branch AND empty path) is inert —
    // it renders the "scenario incomplete" message, not an ending.
    const isBrokenStart = !currentBranch && path.length === 0;
    if (isBrokenStart) return;

    // A restored journey lands directly on the settled ending view — no settle
    // glitch, no scanline develop, restart shown immediately (the learner has
    // already watched this ending resolve). Consumed on the mount run only.
    if (restoredRef.current) {
      restoredRef.current = false;
      setRestartShow(true);
      return;
    }

    // brief holographic settle on every transition
    if (!reduceMotion) {
      setGlitching(true);
      later(() => setGlitching(false), 460);
    }

    // Run the ending reveal for ANY terminal landing — a real ending, a
    // missing-branch dead-end (no currentBranch mid-path), or an empty-choice
    // decision dead-end. All three render the ending layout below, so all three
    // must surface the restart affordance (otherwise a dead-end would strand the
    // learner with no way back). Mirrors the standard renderer treating every
    // such state as a graceful close.
    const landsOnEnding =
      !currentBranch ||
      currentBranch.type === 'ending' ||
      (currentBranch.type === 'decision' && (currentBranch.choices?.length ?? 0) === 0);

    if (landsOnEnding) {
      play('reveal');
      if (reduceMotion) {
        setEndingDeveloping(false);
        setRestartShow(true);
      } else {
        // trigger scanline-develop reveal on the next frame
        const raf = requestAnimationFrame(() => setEndingDeveloping(true));
        timersRef.current.push(raf as unknown as ReturnType<typeof setTimeout>);
        later(() => setRestartShow(true), 1500);
      }
    }
    // decision branches: the <Typewriter> drives its own char animation; the
    // `advance` cue + question/choices reveal fire from its onDone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranchId]);

  const handleTypeDone = useCallback(() => {
    setTypedBranch(currentBranchId);
    play('advance');
  }, [play, currentBranchId]);

  const choose = useCallback(
    (choice: Choice) => {
      if (!currentBranch) return;
      // No capture here — per-choice writes overwrote each other (records are
      // keyed per slide). The path accumulates locally and the single composite
      // record is written when the journey lands on an ending.
      play('select');
      // "consulting the oracle" swell when committing toward an ending
      const target = data.branches.find((b) => b.id === choice.nextBranch);
      if (target?.type === 'ending') play('commit');
      setPath((prev) => [
        ...prev,
        { branchId: currentBranch.id, choiceLetter: choice.letter, pathLabel: choice.pathLabel },
      ]);
      setCurrentBranchId(choice.nextBranch);
    },
    [currentBranch, play, data.branches],
  );

  // Keyboard selection. The design has always PRINTED the choice letters like
  // keycaps ("A", "B", "[ select ▸ ]") without ever binding them, so pressing A
  // did nothing — the affordance was a lie. Only live once the choices are
  // actually revealed, and never while the learner is typing in the reflection
  // box or holding a modifier for a browser shortcut.
  const liveChoices =
    typingDone && currentBranch?.type === 'decision' ? (currentBranch.choices ?? []) : [];
  const liveChoicesKey = liveChoices.map((c) => c.letter).join('');
  useEffect(() => {
    if (liveChoices.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
      const hit = liveChoices.find((c) => c.letter.toLowerCase() === e.key.toLowerCase());
      if (!hit) return;
      e.preventDefault();
      choose(hit);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // Keyed by the letters on offer + the branch, so the handler rebinds when
    // the choices change but not on every unrelated render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveChoicesKey, currentBranchId, choose]);

  const restart = useCallback(() => {
    play('reset');
    setPath([]);
    // Clear the reflection commit so the replay's ending starts fresh — the
    // new journey re-captures from scratch (completion ref resets via effect).
    setReflectionText('');
    setReflectionLogged(false);
    setCurrentBranchId(startId);
  }, [play, startId, setCurrentBranchId]);

  // ── Completion signal + composite journey capture ────────────────────────────
  // Fire onComplete exactly once when the learner reaches a TERMINAL branch (a
  // real ending, a missing-branch dead-end, or an empty-choice decision) — never
  // on each decision. A genuinely broken START (no current branch AND empty path)
  // is not a completion. Computed here, ahead of the early returns, so the hook
  // stays unconditional. Restart resets the ref so a replay can re-complete
  // AND re-capture its new journey. The same guard writes the ONE composite
  // record — the full path plus where it landed — so nothing overwrites it.
  // (Pre-seeded true when rehydrating, so a restored ending never re-fires.)
  const isTerminalBranch =
    (!currentBranch && path.length > 0) ||
    currentBranch?.type === 'ending' ||
    (currentBranch?.type === 'decision' && (currentBranch.choices?.length ?? 0) === 0);
  useEffect(() => {
    if (isTerminalBranch && !completedRef.current) {
      completedRef.current = true;
      onActivityResponse?.({
        activityType: 'scenario-simulation',
        mode: 'quiz',
        responseData: { path, endingId: currentBranchId },
      });
      onComplete?.();
    }
    if (!isTerminalBranch) completedRef.current = false;
  }, [isTerminalBranch, onComplete, onActivityResponse, path, currentBranchId]);

  // Logging the reflection re-emits the full journey with the reflection folded
  // in — records are keyed per slide, so the latest write must carry everything
  // (mirrors RisographPerspectiveLenses' captured synthesis, §8.10).
  const commitReflection = useCallback(() => {
    const text = reflectionText.trim();
    if (text.length === 0) return;
    setReflectionLogged(true);
    play('commit');
    onActivityResponse?.({
      activityType: 'scenario-simulation',
      mode: 'quiz',
      responseData: { path, endingId: currentBranchId, reflection: text },
    });
  }, [reflectionText, path, currentBranchId, onActivityResponse, play]);

  // ── Decision-log scaffold (mirrors Student) ──────────────────────────────────
  // REAL HISTORY ONLY. This used to render one beat per decision branch in the
  // data (F1…F4) — but a branching scenario has no fixed length, so the learner
  // was shown phantom beats down paths they'd never take, and a two-decision run
  // ended with two beats permanently greyed out. Now: one beat per decision
  // actually made, plus a "deciding" beat for the one in front of them. Same
  // rule the standard renderer's trail follows.
  // A choice pointing to a missing branch id, or a decision branch with no
  // choices, dead-ends the learner in a blank box (review doc §3 — reproduced
  // across themes incl. this neon-oracle skin). Only a genuinely broken START
  // shows the incomplete message; a dead-end mid-scenario closes gracefully via
  // the ending layout + safe fallbacks below, so every path resolves.
  const isDeadEndDecision =
    currentBranch?.type === 'decision' && (currentBranch.choices?.length ?? 0) === 0;

  const showLog = path.length > 0;
  const isDecidingNow = !!currentBranch && currentBranch.type === 'decision' && !isDeadEndDecision;

  if (!currentBranch && path.length === 0) {
    return (
      <div
        className="h-full flex items-center justify-center text-sm"
        style={{ background: theme.colors.background, color: theme.colors.textMuted }}
      >
        No starting branch — the scenario is incomplete.
      </div>
    );
  }

  const textColor = theme.colors.text;
  const muted = theme.colors.textMuted;
  const panelEdge = `${muted}33`;
  const isEnding = !currentBranch || currentBranch.type === 'ending' || isDeadEndDecision;
  const endingTitle = currentBranch?.title ?? 'Where that path led';
  // The outcome (what happened) and the lesson (why it matters) are SEPARATE
  // fields — show BOTH. This used to be one `??` chain, which meant any branch
  // authored with a `situation` silently dropped its `consequence`: the actual
  // teaching payload, gone. Same fix, same reason, as ScenarioSimulationSlide.
  const endingOutcome = currentBranch?.situation ?? currentBranch?.description;
  const endingLesson = currentBranch?.consequence;
  const endingReflection = currentBranch?.reflection;
  // Never render an empty close: a branch carrying no prose at all still gets a
  // clear terminal beat rather than a bare title.
  const endingFallback =
    !endingOutcome && !endingLesson && !endingReflection
      ? 'You’ve reached the end of this path.'
      : undefined;

  return (
    <WorldStage
      worldId="neon-oracle"
      theme={theme}
      audioEnabled={audioOn}
      onToggleAudio={toggleAudio}
      className="noracle"
    >
      {/* ===== DECISION-LOG HUD =====
          `top-0`, not `top-12`: this element's containing block is the scroller
          inside <WorldStage>, not the page. A 48px offset (borrowed from the
          lesson viewer's floating nav band) made sticky push the bar DOWN over
          the slide title even at scrollTop 0. */}
      {showLog && (
        <div
          className="sticky top-0 z-20 backdrop-blur"
          style={{
            background: `color-mix(in srgb, ${theme.colors.background} 88%, transparent)`,
            borderBottom: `1px solid ${panelEdge}`,
          }}
        >
          {/* pr-14 keeps the chips clear of the sound toggle pinned top-right;
              at narrow widths they wrap under it otherwise. */}
          <div className="mx-auto max-w-[820px] pl-6 pr-14 py-2 flex items-center gap-2 flex-wrap">
            <span
              className="uppercase"
              style={{ color: muted, fontSize: 10, letterSpacing: '0.26em', fontFamily: monoFont }}
            >
              ▌ oracle.log
            </span>
            {path.map((step, i) => (
              <span
                key={i}
                className="no-beat inline-flex items-center gap-1.5 px-2.5 py-1 text-xs"
                style={{
                  border: `1px solid ${cyan}`,
                  background: `${cyan}10`,
                  color: cyan,
                  fontFamily: monoFont,
                  letterSpacing: '0.08em',
                }}
              >
                <span style={{ fontWeight: 600 }}>{i + 1}</span>
                <span aria-hidden style={{ opacity: 0.6 }}>·</span>
                <span>{step.pathLabel}</span>
              </span>
            ))}
            {isDecidingNow && (
              <span
                className="no-beat no-current inline-flex items-center gap-1.5 px-2.5 py-1 text-xs"
                style={{
                  border: `1px solid ${lime}`,
                  background: `${lime}14`,
                  color: lime,
                  fontFamily: monoFont,
                  letterSpacing: '0.08em',
                }}
              >
                <span style={{ fontWeight: 600 }}>{path.length + 1}</span>
                <span aria-hidden style={{ opacity: 0.6 }}>·</span>
                <span>deciding</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* ===== STAGE ===== */}
      <div className={`no-stage relative${glitching ? ' no-glitching' : ''}`}>
        <div className="mx-auto max-w-[880px] px-6 py-8">
          <header className="mb-5">
            <h1
              style={{
                fontFamily: headingFont,
                fontWeight: 700,
                fontSize: 'clamp(20px, 2.6vw, 30px)',
                color: textColor,
                margin: 0,
                letterSpacing: '0.01em',
              }}
            >
              {data.title}
            </h1>
            {data.subtitle && (
              <p style={{ color: muted, fontSize: '0.9rem', marginTop: 6, letterSpacing: '0.01em' }}>
                {data.subtitle}
              </p>
            )}
          </header>

          {/* ORACLE_TERMINAL panel */}
          <div
            style={{
              position: 'relative',
              border: `1px solid ${panelEdge}`,
              background: `linear-gradient(180deg, ${theme.colors.surface}, ${theme.colors.background})`,
              boxShadow: `0 0 0 1px ${cyan}0d, 0 30px 80px rgba(0,0,0,0.45), inset 0 0 60px ${cyan}08`,
              padding: 'clamp(22px, 3vw, 40px)',
              paddingTop: 56,
            }}
          >
            {/* terminal title bar — fixed 30px tall, so the label must never
                wrap: at narrow widths the trailing ":: CHANNEL [ƒ]" used to
                spill out of the bar and land on top of the content below it. */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 30,
                lineHeight: '30px',
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: muted,
                padding: '0 14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                background: `linear-gradient(90deg, ${cyan}14, transparent)`,
                borderBottom: `1px solid ${panelEdge}`,
                fontFamily: monoFont,
              }}
            >
              ◉ ORACLE_TERMINAL v0.9.7 :: CHANNEL [ƒ]
            </div>

            {isEnding ? (
              <div style={{ textAlign: 'left', position: 'relative' }}>
                <div className={`no-reveal-wrap${endingDeveloping ? ' no-developing' : ''}`} style={{ position: 'relative' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      fontFamily: monoFont,
                      fontSize: 12,
                      letterSpacing: '0.16em',
                      padding: '6px 14px',
                      marginBottom: 18,
                      border: `1px solid ${cyan}`,
                      color: cyan,
                      textTransform: 'lowercase',
                    }}
                  >
                    // outcome
                  </span>
                  {endingTitle && (
                    <h2
                      style={{
                        fontFamily: headingFont,
                        fontSize: 'clamp(24px, 3.6vw, 40px)',
                        lineHeight: 1.12,
                        marginBottom: 20,
                        color: textColor,
                        textShadow: `0 0 24px ${cyan}40`,
                      }}
                    >
                      {endingTitle}
                    </h2>
                  )}
                  {(endingOutcome || endingFallback) && (
                    <p
                      style={{
                        fontSize: 'clamp(15px, 1.7vw, 18px)',
                        lineHeight: 1.75,
                        color: textColor,
                        maxWidth: '62ch',
                        marginBottom: 0,
                      }}
                    >
                      {endingOutcome ?? endingFallback}
                    </p>
                  )}
                  {/* The lesson — why the outcome happened. A separate beat, set
                      apart by the lime rule so it doesn't read as more narration. */}
                  {endingLesson && (
                    <div
                      style={{
                        marginTop: 22,
                        paddingLeft: 16,
                        borderLeft: `2px solid ${lime}`,
                        maxWidth: '62ch',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: monoFont,
                          fontSize: 10,
                          letterSpacing: '0.26em',
                          textTransform: 'uppercase',
                          color: lime,
                          marginBottom: 8,
                        }}
                      >
                        ▚ why
                      </div>
                      <p
                        style={{
                          fontSize: 'clamp(14px, 1.55vw, 16.5px)',
                          lineHeight: 1.7,
                          color: textColor,
                          margin: 0,
                        }}
                      >
                        {endingLesson}
                      </p>
                    </div>
                  )}
                </div>

                {endingReflection && (
                  <div
                    style={{
                      marginTop: 22,
                      padding: 14,
                      background: `${cyan}10`,
                      border: `1px solid ${cyan}33`,
                      // The prompt is prose the learner has to read and answer,
                      // so it takes full text colour, not the muted chrome tone.
                      color: textColor,
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    <p
                      style={{
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: cyan,
                        marginBottom: 6,
                        fontWeight: 700,
                      }}
                    >
                      Reflect
                    </p>
                    {endingReflection}
                    {/* real commit, not display-only — the answer is folded into
                        the composite journey record on log (handover §8.8) */}
                    <textarea
                      value={reflectionText}
                      onChange={(e) => setReflectionText(e.target.value)}
                      disabled={reflectionLogged}
                      rows={3}
                      placeholder="Type your reading of this vision"
                      aria-label="Your reflection"
                      style={{
                        display: 'block',
                        width: '100%',
                        marginTop: 12,
                        padding: '10px 12px',
                        background: `${theme.colors.background}66`,
                        border: `1px solid ${cyan}33`,
                        color: textColor,
                        fontFamily: bodyFont,
                        fontSize: 14,
                        lineHeight: 1.6,
                        resize: 'vertical',
                        opacity: reflectionLogged ? 0.7 : 1,
                      }}
                    />
                    <div style={{ marginTop: 10 }}>
                      {!reflectionLogged ? (
                        <button
                          type="button"
                          onClick={commitReflection}
                          disabled={reflectionText.trim().length === 0}
                          style={{
                            fontFamily: monoFont,
                            fontSize: 11,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            background: 'transparent',
                            color: cyan,
                            border: `1px solid ${cyan}`,
                            padding: '9px 16px',
                            cursor: reflectionText.trim().length === 0 ? 'not-allowed' : 'pointer',
                            opacity: reflectionText.trim().length === 0 ? 0.45 : 1,
                          }}
                        >
                          log reflection
                        </button>
                      ) : (
                        <span
                          style={{
                            fontFamily: monoFont,
                            fontSize: 11,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: lime,
                          }}
                        >
                          ✓ reflection logged
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: 28,
                    paddingTop: 18,
                    borderTop: `1px solid ${panelEdge}`,
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: muted,
                    fontFamily: monoFont,
                  }}
                >
                  vision resolved along path{'  '}
                  {/* The authored pathLabels, not `start→A :: approve→A`: raw
                      branch ids are an internal key and mean nothing to a
                      learner reading their own trail back. */}
                  <b style={{ color: cyan, fontWeight: 500 }}>
                    {path.length ? path.map((p) => p.pathLabel).join('  ::  ') : '—'}
                  </b>
                </div>

                <button
                  type="button"
                  onClick={restart}
                  className={`no-restart${restartShow ? ' no-show' : ''}`}
                  style={{
                    marginTop: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12,
                    fontFamily: monoFont,
                    fontSize: 13,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    background: 'transparent',
                    color: lime,
                    border: `1px solid ${lime}`,
                    padding: '13px 22px',
                    cursor: 'pointer',
                  }}
                >
                  <span aria-hidden style={{ fontSize: 15 }}>↺</span> restart oracle
                </button>
              </div>
            ) : currentBranch ? (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 18,
                  }}
                >
                  <div
                    className="no-blip"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10.5,
                      letterSpacing: '0.26em',
                      textTransform: 'uppercase',
                      color: cyan,
                      textShadow: `0 0 8px ${cyan}80`,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {currentBranch.label || 'incoming transmission'}
                  </div>
                  {/* Skip lives in the header, not beside the caret: an inline
                      control would slide down the panel as the text grows. */}
                  {!typingDone && !reduceMotion && (
                    <button
                      type="button"
                      onClick={() => setSkipSignal((n) => n + 1)}
                      style={{
                        marginLeft: 'auto',
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        background: 'transparent',
                        color: muted,
                        border: `1px solid ${muted}66`,
                        padding: '4px 10px',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                    >
                      skip ▸
                    </button>
                  )}
                </div>

                <Typewriter
                  key={currentBranchId}
                  text={decisionText}
                  speed={28}
                  onDone={handleTypeDone}
                  caretColor={cyan}
                  skipSignal={skipSignal}
                  style={{
                    display: 'block',
                    fontFamily: monoFont,
                    fontSize: 'clamp(15px, 1.7vw, 18px)',
                    lineHeight: 1.7,
                    color: textColor,
                    minHeight: '4.2em',
                    marginBottom: 22,
                    whiteSpace: 'pre-wrap',
                  }}
                />

                {/* The exhibit — the artifact the decisions revolve around,
                    printed as a document readout at the entry branch (after
                    the transmission types out) so no later question refers to
                    something never on screen. Static text: a document should
                    be consultable at a glance, not re-typed. */}
                {data.exhibit && path.length === 0 && (
                  <div
                    className={`no-question${typingDone ? ' no-show' : ''}`}
                    style={{
                      border: `1px solid ${lime}55`,
                      background: `color-mix(in srgb, ${lime} 5%, transparent)`,
                      boxShadow: `inset 0 0 22px ${lime}0e`,
                      padding: '14px 18px',
                      margin: '0 0 24px',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: '0.26em',
                        textTransform: 'uppercase',
                        color: lime,
                        textShadow: `0 0 8px ${lime}80`,
                        marginBottom: 10,
                      }}
                    >
                      ▚ exhibit :: {data.exhibit.kind}
                      {data.exhibit.title ? ` — ${data.exhibit.title}` : ''}
                    </div>
                    <div
                      style={{
                        // The exhibit IS a document readout, so it keeps mono.
                        fontFamily: monoFont,
                        fontSize: 'clamp(13px, 1.4vw, 15.5px)',
                        lineHeight: 1.65,
                        color: textColor,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {data.exhibit.body}
                    </div>
                    {data.exhibit.attribution && (
                      <div style={{ fontSize: 11, color: muted, marginTop: 10, fontStyle: 'italic' }}>
                        {data.exhibit.attribution}
                      </div>
                    )}
                  </div>
                )}

                {currentBranch.question && (
                  <h2
                    className={`no-question${typingDone ? ' no-show' : ''}`}
                    style={{
                      fontFamily: headingFont,
                      fontSize: 'clamp(20px, 2.8vw, 30px)',
                      color: textColor,
                      lineHeight: 1.25,
                      margin: '4px 0 26px',
                    }}
                  >
                    {currentBranch.question}
                  </h2>
                )}

                {/* Choices are hidden (opacity 0) until the transmission has
                    typed out. They used to stay clickable and keyboard-focusable
                    the whole time, so you could Tab to an invisible button or
                    click empty space and skip the scenario forward by accident.
                    `inert` takes them out of pointer, focus and a11y tree
                    together until they're actually on screen. */}
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 11 }}
                  inert={!typingDone}
                >
                  {(currentBranch.choices ?? []).map((choice, idx) => (
                    <button
                      key={choice.letter}
                      type="button"
                      onClick={() => choose(choice)}
                      onMouseEnter={() => play('hover')}
                      className={`no-choice${typingDone ? ' no-reveal' : ''}`}
                      style={{
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 14,
                        background: `${theme.colors.background}66`,
                        // Was `${muted}44` — a ~1.1:1 edge, so the rows barely
                        // read as separate targets at all.
                        border: `1px solid ${muted}66`,
                        color: textColor,
                        padding: '16px 18px',
                        fontFamily: bodyFont,
                        fontSize: 15,
                        lineHeight: 1.55,
                        letterSpacing: '0',
                        cursor: 'pointer',
                        transitionDelay: reduceMotion ? '0ms' : `${120 + idx * 110}ms`,
                      }}
                    >
                      <span
                        className="no-key"
                        aria-hidden
                        style={{
                          flex: '0 0 auto',
                          fontFamily: monoFont,
                          fontWeight: 700,
                          fontSize: 15,
                          color: muted,
                          width: 22,
                        }}
                      >
                        {choice.letter}
                      </span>
                      <span style={{ flex: 1 }}>{choice.text}</span>
                      <span
                        className="no-arrow"
                        aria-hidden
                        style={{
                          marginLeft: 'auto',
                          fontFamily: monoFont,
                          color: muted,
                          opacity: 0,
                          fontSize: 13,
                          letterSpacing: '0.1em',
                          alignSelf: 'center',
                          transition: 'all 0.16s',
                        }}
                      >
                        [ {choice.letter} ▸ ]
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* Status strip — pure set dressing, so it's hidden from assistive
              tech rather than read out as content. Full `muted` (no trailing
              alpha): the old `${muted}aa` landed at 2.53:1. */}
          <div
            aria-hidden
            style={{
              display: 'flex',
              gap: 24,
              alignItems: 'center',
              fontSize: 10,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: muted,
              paddingTop: 16,
              fontFamily: monoFont,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="no-dot" /> oracle online
            </span>
            <span style={{ flex: 1 }} />
            <span>channel ƒ // locked</span>
          </div>
        </div>
      </div>
    </WorldStage>
  );
}
